import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHasTwoFactorAuth } from "hooks/featureFlags";
import { useCurrentUser } from "hooks/auth";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { selectRecoveryReenrolPrompt } from "newui/features/Auth/authSelectors";
import {
	markTwoFactorEnrolled,
	setRecoveryReenrolPrompt,
} from "newui/features/Auth/authSlice";
import { Enforced2faSetupModal } from "./Enforced2faSetupModal";
import { RecoveryReenrolModal } from "./RecoveryReenrolModal";

/**
 * App-shell gate for company-enforced 2FA. Mounted inside the authenticated shell;
 * when the user's company enforces 2FA and they have not enrolled, it renders an
 * un-dismissable enrolment modal that blocks the app until they enrol or log out.
 *
 * The requirement is read straight off `currentUser` (shipped by UserResource as
 * `two_factor_setup_required`) so the modal shows instantly at login with NO extra
 * request. Admin-enables-enforcement and post-enrolment transitions update the same
 * flag optimistically in redux, so the gate stays request-free.
 *
 * Renders nothing when the feature flag is off or the user is not required to set up.
 */
export const TwoFactorGate = () => {
	const hasTwoFactorAuth = useHasTwoFactorAuth();
	const dispatch = useDispatch();
	const { logoutUser } = useAuthContext();
	const { currentUser } = useCurrentUser() || {};
	const recoveryReenrolPrompt = useSelector(selectRecoveryReenrolPrompt);

	const setupRequired = Boolean(currentUser?.two_factor_setup_required);

	// Latch the modal open once enrolment is required. Completing enrolment clears
	// `two_factor_setup_required`, so we must not derive visibility directly from it
	// mid-wizard — otherwise the modal would unmount before the user can save their
	// recovery codes. `completed` prevents the effect re-opening it afterwards.
	const [isOpen, setIsOpen] = useState(false);
	const [completed, setCompleted] = useState(false);

	useEffect(() => {
		if (hasTwoFactorAuth && setupRequired && !completed) {
			setIsOpen(true);
		}
	}, [hasTwoFactorAuth, setupRequired, completed]);

	// Enforcement is blocking and takes precedence over the (dismissable) recovery
	// re-enrolment prompt.
	if (isOpen) {
		return (
			<Enforced2faSetupModal
				isOpen
				onComplete={() => {
					setCompleted(true);
					setIsOpen(false);
					dispatch(markTwoFactorEnrolled());
				}}
				onLogout={() => logoutUser()}
			/>
		);
	}

	if (hasTwoFactorAuth && recoveryReenrolPrompt) {
		return (
			<RecoveryReenrolModal
				isOpen
				onClose={() => dispatch(setRecoveryReenrolPrompt(false))}
			/>
		);
	}

	return null;
};
